// Seeded gradient (simplex) noise + fBm + domain-warp helpers (GEN-2).
// Own implementation (no noise library, ARCH §1). Permutation table is shuffled
// from an Rng so a seed fully determines the field. Pinned by committed goldens.
import { Rng } from './rng';

const GRAD3 = [
  [1, 1, 0], [-1, 1, 0], [1, -1, 0], [-1, -1, 0],
  [1, 0, 1], [-1, 0, 1], [1, 0, -1], [-1, 0, -1],
  [0, 1, 1], [0, -1, 1], [0, 1, -1], [0, -1, -1],
] as const;

const F2 = 0.5 * (Math.sqrt(3) - 1);
const G2 = (3 - Math.sqrt(3)) / 6;
const F3 = 1 / 3;
const G3 = 1 / 6;

export class Simplex {
  private readonly perm = new Uint8Array(512);
  private readonly permMod12 = new Uint8Array(512);

  constructor(seed: number | bigint | string) {
    const p = new Uint8Array(256);
    for (let i = 0; i < 256; i++) p[i] = i;
    // Fisher-Yates shuffle driven by the seed.
    const rng = new Rng(seed);
    for (let i = 255; i > 0; i--) {
      const j = rng.nextInt(i + 1);
      const tmp = p[i]!;
      p[i] = p[j]!;
      p[j] = tmp;
    }
    for (let i = 0; i < 512; i++) {
      this.perm[i] = p[i & 255]!;
      this.permMod12[i] = this.perm[i]! % 12;
    }
  }

  /** 2D simplex noise in roughly [-1, 1]. */
  noise2(xin: number, yin: number): number {
    const perm = this.perm;
    const permMod12 = this.permMod12;
    const s = (xin + yin) * F2;
    const i = Math.floor(xin + s);
    const j = Math.floor(yin + s);
    const t = (i + j) * G2;
    const x0 = xin - (i - t);
    const y0 = yin - (j - t);

    let i1: number, j1: number;
    if (x0 > y0) { i1 = 1; j1 = 0; } else { i1 = 0; j1 = 1; }

    const x1 = x0 - i1 + G2;
    const y1 = y0 - j1 + G2;
    const x2 = x0 - 1 + 2 * G2;
    const y2 = y0 - 1 + 2 * G2;

    const ii = i & 255;
    const jj = j & 255;
    const gi0 = permMod12[ii + perm[jj]!]!;
    const gi1 = permMod12[ii + i1 + perm[jj + j1]!]!;
    const gi2 = permMod12[ii + 1 + perm[jj + 1]!]!;

    let n0 = 0, n1 = 0, n2 = 0;
    let t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 >= 0) { t0 *= t0; n0 = t0 * t0 * (GRAD3[gi0]![0] * x0 + GRAD3[gi0]![1] * y0); }
    let t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 >= 0) { t1 *= t1; n1 = t1 * t1 * (GRAD3[gi1]![0] * x1 + GRAD3[gi1]![1] * y1); }
    let t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 >= 0) { t2 *= t2; n2 = t2 * t2 * (GRAD3[gi2]![0] * x2 + GRAD3[gi2]![1] * y2); }

    return 70 * (n0 + n1 + n2);
  }

  /** 3D simplex noise in roughly [-1, 1]. */
  noise3(xin: number, yin: number, zin: number): number {
    const perm = this.perm;
    const permMod12 = this.permMod12;
    const s = (xin + yin + zin) * F3;
    const i = Math.floor(xin + s);
    const j = Math.floor(yin + s);
    const k = Math.floor(zin + s);
    const t = (i + j + k) * G3;
    const x0 = xin - (i - t);
    const y0 = yin - (j - t);
    const z0 = zin - (k - t);

    let i1: number, j1: number, k1: number;
    let i2: number, j2: number, k2: number;
    if (x0 >= y0) {
      if (y0 >= z0) { i1 = 1; j1 = 0; k1 = 0; i2 = 1; j2 = 1; k2 = 0; }
      else if (x0 >= z0) { i1 = 1; j1 = 0; k1 = 0; i2 = 1; j2 = 0; k2 = 1; }
      else { i1 = 0; j1 = 0; k1 = 1; i2 = 1; j2 = 0; k2 = 1; }
    } else {
      if (y0 < z0) { i1 = 0; j1 = 0; k1 = 1; i2 = 0; j2 = 1; k2 = 1; }
      else if (x0 < z0) { i1 = 0; j1 = 1; k1 = 0; i2 = 0; j2 = 1; k2 = 1; }
      else { i1 = 0; j1 = 1; k1 = 0; i2 = 1; j2 = 1; k2 = 0; }
    }

    const x1 = x0 - i1 + G3, y1 = y0 - j1 + G3, z1 = z0 - k1 + G3;
    const x2 = x0 - i2 + 2 * G3, y2 = y0 - j2 + 2 * G3, z2 = z0 - k2 + 2 * G3;
    const x3 = x0 - 1 + 3 * G3, y3 = y0 - 1 + 3 * G3, z3 = z0 - 1 + 3 * G3;

    const ii = i & 255, jj = j & 255, kk = k & 255;
    const gi0 = permMod12[ii + perm[jj + perm[kk]!]!]!;
    const gi1 = permMod12[ii + i1 + perm[jj + j1 + perm[kk + k1]!]!]!;
    const gi2 = permMod12[ii + i2 + perm[jj + j2 + perm[kk + k2]!]!]!;
    const gi3 = permMod12[ii + 1 + perm[jj + 1 + perm[kk + 1]!]!]!;

    let n0 = 0, n1 = 0, n2 = 0, n3 = 0;
    let t0 = 0.6 - x0 * x0 - y0 * y0 - z0 * z0;
    if (t0 >= 0) { t0 *= t0; n0 = t0 * t0 * (GRAD3[gi0]![0] * x0 + GRAD3[gi0]![1] * y0 + GRAD3[gi0]![2] * z0); }
    let t1 = 0.6 - x1 * x1 - y1 * y1 - z1 * z1;
    if (t1 >= 0) { t1 *= t1; n1 = t1 * t1 * (GRAD3[gi1]![0] * x1 + GRAD3[gi1]![1] * y1 + GRAD3[gi1]![2] * z1); }
    let t2 = 0.6 - x2 * x2 - y2 * y2 - z2 * z2;
    if (t2 >= 0) { t2 *= t2; n2 = t2 * t2 * (GRAD3[gi2]![0] * x2 + GRAD3[gi2]![1] * y2 + GRAD3[gi2]![2] * z2); }
    let t3 = 0.6 - x3 * x3 - y3 * y3 - z3 * z3;
    if (t3 >= 0) { t3 *= t3; n3 = t3 * t3 * (GRAD3[gi3]![0] * x3 + GRAD3[gi3]![1] * y3 + GRAD3[gi3]![2] * z3); }

    return 32 * (n0 + n1 + n2 + n3);
  }
}

export interface FbmOptions {
  octaves?: number;
  lacunarity?: number;
  gain?: number;
  frequency?: number;
}

/** Fractional Brownian motion over 2D simplex; result normalized to ~[-1, 1]. */
export function fbm2(noise: Simplex, x: number, z: number, opts: FbmOptions = {}): number {
  const { octaves = 4, lacunarity = 2, gain = 0.5, frequency = 1 } = opts;
  let sum = 0, amp = 1, freq = frequency, norm = 0;
  for (let o = 0; o < octaves; o++) {
    sum += amp * noise.noise2(x * freq, z * freq);
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return sum / norm;
}

/** Fractional Brownian motion over 3D simplex; result normalized to ~[-1, 1]. */
export function fbm3(noise: Simplex, x: number, y: number, z: number, opts: FbmOptions = {}): number {
  const { octaves = 4, lacunarity = 2, gain = 0.5, frequency = 1 } = opts;
  let sum = 0, amp = 1, freq = frequency, norm = 0;
  for (let o = 0; o < octaves; o++) {
    sum += amp * noise.noise3(x * freq, y * freq, z * freq);
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return sum / norm;
}

/** Domain warp: offset the sample point by a second noise lookup (GEN-2 helper). */
export function domainWarp2(
  noise: Simplex,
  x: number,
  z: number,
  amp: number,
  freq: number,
): [number, number] {
  const wx = noise.noise2((x + 3.1) * freq, (z + 1.7) * freq);
  const wz = noise.noise2((x - 2.3) * freq, (z - 4.9) * freq);
  return [x + wx * amp, z + wz * amp];
}
